import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Droplets, LogOut, MapPin, Phone, Navigation, CheckCircle2, Loader2,
  Package, Truck, RefreshCw, Fuel, FileText, X, PlusCircle, Calendar,
} from "lucide-react";
import { ManualOrderModal } from "../components/ManualOrderModal";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ServiceAreaManager from "@/components/ServiceAreaManager";

const SERVICE_LABEL = { pool: "Pool Filling", tank: "Tank Refill", construction: "Construction", emergency: "Emergency" };
const STATUS_STYLE = {
  new: "bg-slate-100 text-slate-600",
  assigned: "bg-blue-50 text-blue-700 border border-blue-200",
  in_transit: "bg-amber-50 text-amber-700 border border-amber-200",
  delivered: "bg-green-50 text-green-700 border border-green-200",
};
const STATUS_LABEL = { new: "New", assigned: "Assigned", in_transit: "In Transit", delivered: "Delivered" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });
};

const STATION_LIST = [
  { region: "Rodney", label: "Silverdale — 3 Bluegum Avenue (drinking water only)", lat: -36.617, lng: 174.667 },
  { region: "Rodney", label: "Wellsford — 103 Centennial Park Road", lat: -36.293, lng: 174.526 },
  { region: "Rodney", label: "Warkworth — 6 Brown Road", lat: -36.398, lng: 174.663 },
  { region: "Rodney", label: "Helensville — 37 Mill Road", lat: -36.678, lng: 174.451 },
  { region: "Rodney", label: "Peter Snell Village — Opposite 1191 Whangaparaoa Road", lat: -36.617, lng: 174.747 },
  { region: "Rodney", label: "Whangaparaoa — 651 Whangaparaoa Road", lat: -36.617, lng: 174.780 },
  { region: "North Shore", label: "Albany — 21-31 Oteha Valley Road Extension (2 pumps)", lat: -36.729, lng: 174.700 },
  { region: "North Shore", label: "Paremoremo — 31 Attwood Road", lat: -36.735, lng: 174.643 },
  { region: "Waitākere", label: "Massey — 7-9 Westgate Drive", lat: -36.815, lng: 174.610 },
];

const STATIONS = STATION_LIST.reduce((acc, s) => { (acc[s.region] = acc[s.region] || []).push(s.label); return acc; }, {});

const SUBURB_COORDS = {
  "Albany": [-36.729, 174.700], "Bethells Beach": [-36.887, 174.452], "Coatesville": [-36.717, 174.645],
  "Dairy Flat": [-36.665, 174.660], "Fairview Heights": [-36.717, 174.717], "Greenhithe": [-36.783, 174.660],
  "Helensville": [-36.678, 174.451], "Hobsonville": [-36.795, 174.660], "Huapai": [-36.767, 174.510],
  "Kumeū": [-36.775, 174.523], "Kanohi": [-36.600, 174.440], "Kaukapakapa": [-36.615, 174.470],
  "Lucas Heights": [-36.710, 174.720], "Long Bay": [-36.680, 174.750], "Muriwai": [-36.827, 174.435],
  "Massey": [-36.833, 174.600], "Northcross": [-36.710, 174.720], "Oteha": [-36.720, 174.710],
  "Paremoremo": [-36.735, 174.643], "Pinehill": [-36.730, 174.720], "Riverhead": [-36.750, 174.600],
  "Rosedale": [-36.740, 174.700], "Redvale": [-36.640, 174.680], "Silverdale": [-36.617, 174.667],
  "Stillwater": [-36.630, 174.710], "Taupaki": [-36.800, 174.550], "Te Henga (Bethells)": [-36.887, 174.452],
  "Whenuapai": [-36.790, 174.630], "Waimauku": [-36.780, 174.480], "Woodhill": [-36.750, 174.440],
  "Waitakere": [-36.850, 174.530], "Waitoki": [-36.580, 174.550], "Wainui": [-36.550, 174.630],
  "Wharepapa (Helensville)": [-36.678, 174.451], "West Harbour": [-36.820, 174.630], "Westgate": [-36.810, 174.610],
};

const _hav = ([la1, lo1], [la2, lo2]) => {
  const R = 6371, r = Math.PI / 180;
  const dLa = (la2 - la1) * r, dLo = (lo2 - lo1) * r;
  const a = Math.sin(dLa / 2) ** 2 + Math.cos(la1 * r) * Math.cos(la2 * r) * Math.sin(dLo / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const nearestStation = (suburb) => {
  const c = SUBURB_COORDS[suburb];
  if (!c) return null;
  let best = null, bd = Infinity;
  for (const s of STATION_LIST) {
    const d = _hav(c, [s.lat, s.lng]);
    if (d < bd) { bd = d; best = s; }
  }
  return best;
};

export default function Driver() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [docketTarget, setDocketTarget] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [tab, setTab] = useState("loads");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/loads");
      setLoads(data);
    } catch (e) {
      toast.error("Could not load your deliveries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const { data } = await api.patch(`/driver/orders/${id}/status`, { order_status: status });
      setLoads((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success(status === "delivered" ? "Marked as delivered" : "Route started");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const setStation = async (id, station) => {
    try {
      const { data } = await api.patch(`/driver/orders/${id}/station`, { station });
      setLoads((prev) => prev.map((o) => (o.id === id ? data : o)));
      if (station) toast.success("Filling station set");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const active = loads.filter((o) => o.order_status !== "delivered");
  const done = loads.filter((o) => o.order_status === "delivered");

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="brand-gradient w-9 h-9 rounded-xl flex items-center justify-center shadow-sm">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold leading-none text-slate-900">Driver Portal</div>
              <div className="text-xs text-slate-500 mt-0.5">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setShowManual(true)} data-testid="driver-new-load-btn"
              className="text-sm font-semibold brand-gradient text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" title="New load">
              <PlusCircle className="w-4 h-4" /> <span className="hidden sm:inline">New Load</span>
            </button>
            <button onClick={load} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" data-testid="driver-refresh-btn" title="Refresh">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button onClick={doLogout} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 flex items-center gap-1.5 text-sm font-medium transition-colors" data-testid="driver-logout-btn">
              <LogOut className="w-5 h-5" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-7">
        <div className="inline-flex gap-1 mb-6 bg-white border border-slate-200 rounded-full p-1 shadow-sm">
          <button onClick={() => setTab("loads")} data-testid="driver-tab-loads"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "loads" ? "brand-gradient text-white shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>My Loads</button>
          <button onClick={() => setTab("areas")} data-testid="driver-tab-areas"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "areas" ? "brand-gradient text-white shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Areas & Pricing</button>
        </div>

        {tab === "areas" && (
          <div data-testid="driver-areas-panel"><ServiceAreaManager /></div>
        )}

        {tab === "loads" && (<>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-7">
            <Stat icon={Truck} label="Active" value={active.length} tone="amber" />
            <Stat icon={CheckCircle2} label="Delivered" value={done.length} tone="green" />
            <Stat icon={Package} label="Total Loads" value={loads.length} tone="blue" />
          </div>

          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
          ) : (
            <>
              <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2 text-slate-900">
                <Package className="w-5 h-5 text-blue-600" /> Today's Loads
              </h2>
              {active.length === 0 && <Empty text="No active loads right now. Enjoy the break!" />}
              <div className="grid md:grid-cols-2 gap-5">
                {active.map((o) => { const suggested = nearestStation(o.suburb); return (
                  <div key={o.id} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`driver-load-${o.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-lg text-slate-900 truncate">{o.customer_name}</div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[o.order_status]}`}>
                            {STATUS_LABEL[o.order_status]}
                          </span>
                          <span className="text-xs text-slate-500">{SERVICE_LABEL[o.service_type]} · {o.loads || 1} load{(o.loads || 1) > 1 ? "s" : ""}</span>
                        </div>
                      </div>
                      <span className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full ${o.water_type === "pure" ? "bg-cyan-50 text-cyan-700 border border-cyan-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                        {o.water_type === "pure" ? "Pure Water" : "Town Water"}
                      </span>
                    </div>
                    <div className="space-y-2.5 text-sm mb-5">
                      <div className="flex items-center gap-2.5" data-testid={`driver-load-date-${o.id}`}><Calendar className="w-4 h-4 text-blue-500 shrink-0" /> <span className="font-semibold text-slate-800">{fmtDate(o.scheduled_date)}</span></div>
                      <div className="flex items-start gap-2.5"><MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> <span className="text-slate-700">{o.address}, {o.suburb}</span></div>
                      {o.hose_length != null && (
                        <div className="flex items-center gap-2.5" data-testid={`driver-load-hose-${o.id}`}>
                          <span className="w-4 h-4 shrink-0 rounded-full border-2 border-slate-400" />
                          <span className={`font-semibold ${o.hose_length > 30 ? "text-amber-700" : "text-slate-700"}`}>Hose: {o.hose_length}m</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2.5">
                        <Fuel className="w-4 h-4 text-slate-400 mt-1.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <select value={o.filling_station || ""} onChange={(e) => setStation(o.id, e.target.value)}
                            data-testid={`driver-station-select-${o.id}`}
                            className={`w-full text-sm border rounded-lg px-2 py-1.5 outline-none focus:border-blue-500 bg-white ${o.filling_station ? "border-blue-300 text-slate-800 font-medium" : "border-slate-200 text-slate-500"}`}>
                            <option value="">Select filling station…</option>
                            {Object.entries(STATIONS).map(([region, list]) => (
                              <optgroup key={region} label={region}>
                                {list.map((s) => <option key={s} value={s}>{s}</option>)}
                              </optgroup>
                            ))}
                          </select>
                          {!o.filling_station && suggested && (
                            <button onClick={() => setStation(o.id, suggested.label)} data-testid={`driver-station-suggest-${o.id}`}
                              className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline">
                              <MapPin className="w-3 h-3" /> Nearest: {suggested.label.split(" — ")[0]} — tap to use
                            </button>
                          )}
                        </div>
                      </div>
                      <a href={`tel:${o.customer_phone}`} className="flex items-center gap-2.5 text-slate-700 hover:text-blue-600 transition-colors w-fit"><Phone className="w-4 h-4 text-slate-400 shrink-0" /> {o.customer_phone}</a>
                      <div className="flex items-center gap-2.5">
                        <Droplets className="w-4 h-4 text-slate-400 shrink-0" />
                        {o.payment_method === "cod" ? (
                          <span className="text-amber-600 font-semibold">Collect ${o.amount} on delivery</span>
                        ) : (
                          <span className="text-green-600 font-semibold">Paid online (${o.amount})</span>
                        )}
                      </div>
                      {o.notes && <div className="text-slate-500 italic bg-slate-50 rounded-lg px-3 py-2">Note: {o.notes}</div>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.address + ", " + o.suburb + ", Auckland")}`}
                        target="_blank" rel="noreferrer"
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-colors"
                        data-testid={`driver-navigate-${o.id}`}
                      >
                        <Navigation className="w-5 h-5" /> Navigate
                      </a>
                      {o.order_status !== "in_transit" ? (
                        <button
                          onClick={() => updateStatus(o.id, "in_transit")}
                          disabled={busy === o.id + "in_transit"}
                          className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                          data-testid={`driver-start-${o.id}`}
                        >
                          {busy === o.id + "in_transit" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Truck className="w-5 h-5" /> Start Route</>}
                        </button>
                      ) : (
                        <button
                          onClick={() => setDocketTarget(o)}
                          className="bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                          data-testid={`driver-status-delivered-btn-${o.id}`}
                        >
                          <FileText className="w-5 h-5" /> Complete &amp; Docket
                        </button>
                      )}
                    </div>
                  </div>
                ); })}
              </div>

              {done.length > 0 && (
                <>
                  <h2 className="font-heading font-bold text-lg mt-10 mb-4 flex items-center gap-2 text-slate-900">
                    <CheckCircle2 className="w-5 h-5 text-green-600" /> Completed
                  </h2>
                  <div className="space-y-2.5">
                    {done.map((o) => (
                      <div key={o.id} className="bg-white rounded-xl px-5 py-3.5 flex items-center justify-between border border-slate-200" data-testid={`driver-done-${o.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          <span className="font-semibold text-slate-800 truncate">{o.customer_name}</span>
                          <span className="text-slate-400 text-sm">{o.suburb}</span>
                          {o.filling_station && <span className="hidden sm:inline text-xs text-slate-400 truncate">· {o.filling_station.split(" — ")[0]}</span>}
                        </div>
                        <span className="text-slate-600 text-sm font-semibold shrink-0">{o.water_type === "pure" ? "Pure" : "Town"} · ${o.amount}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>)}
      </div>
      {docketTarget && <DocketModal order={docketTarget} onClose={() => setDocketTarget(null)}
        onDone={(updated) => { setLoads((prev) => prev.map((o) => (o.id === updated.id ? updated : o))); setDocketTarget(null); toast.success("Delivered — docket sent to customer"); }} />}
      {showManual && <ManualOrderModal onClose={() => setShowManual(false)} onCreated={() => load()} />}
    </div>
  );
}

const TONE = {
  amber: "bg-amber-50 text-amber-600",
  green: "bg-green-50 text-green-600",
  blue: "bg-blue-50 text-blue-600",
};

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200 shadow-sm flex items-center gap-3.5">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${TONE[tone]}`}><Icon className="w-5 h-5" /></div>
    <div>
      <div className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 leading-none">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  </div>
);

const Empty = ({ text }) => (
  <div className="bg-white rounded-2xl p-10 text-center text-slate-400 border border-dashed border-slate-200">{text}</div>
);

const PAY_OPTIONS = ["Online Order", "EFTPOS", "Cash", "To be Invoiced", "WINZ Payment"];
const PAY_DEFAULT = { card: "Online Order", cod: "Cash", credit: "Online Order" };

function DocketModal({ order, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    litres: String(order.litres || 11000),
    payment_type: PAY_DEFAULT[order.payment_method] || "Online Order",
    amount_due: order.amount ?? "",
    amount_paid: order.payment_status === "paid" ? (order.amount ?? "") : "",
    notes: order.notes || "",
    water_carrier_registration: "WC00476",
    truck_registration: "",
    water_source: "",
    water_class: "",
    treatment_plant_grading: "",
    reticulation: "",
    signed_by: order.driver_name || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/driver/orders/${order.id}/docket`, {
        ...f,
        amount_due: f.amount_due === "" ? null : Number(f.amount_due),
        amount_paid: f.amount_paid === "" ? null : Number(f.amount_paid),
      });
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSaving(false);
    }
  };

  const Fld = ({ label, k, type = "text", placeholder }) => (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
      <input type={type} value={f[k]} onChange={set(k)} placeholder={placeholder} data-testid={`docket-${k}`}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()} data-testid="docket-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Delivery Docket</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Tax Invoice &amp; Delivery Docket for <span className="font-semibold">{order.customer_name}</span> — {order.suburb}. Sent to the customer on completion.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Fld label="Litres of Water" k="litres" />
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Payment Type</label>
            <select value={f.payment_type} onChange={set("payment_type")} data-testid="docket-payment_type"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
              {PAY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <Fld label="Amount Due ($ Inc. GST)" k="amount_due" type="number" />
          <Fld label="Amount Paid ($)" k="amount_paid" type="number" />
          <Fld label="Water Carrier Registration" k="water_carrier_registration" />
          <Fld label="Truck Registration" k="truck_registration" placeholder="e.g. ABC123" />
          <Fld label="Water Source" k="water_source" />
          <Fld label="Class" k="water_class" />
          <Fld label="Treatment Plant Grading" k="treatment_plant_grading" />
          <Fld label="Reticulation" k="reticulation" />
          <Fld label="Signed by" k="signed_by" />
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Notes for Customer</label>
          <textarea value={f.notes} onChange={set("notes")} rows={2} data-testid="docket-notes"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white" />
        </div>
        <button onClick={submit} disabled={saving} data-testid="docket-submit-btn"
          className="brand-gradient btn-shine text-white font-semibold w-full py-3 rounded-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5" /> Complete &amp; Send Docket</>}
        </button>
      </div>
    </div>
  );
}
